/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { logger } from "matrix-js-sdk/lib/logger";

import {
  createInitialSession,
  getRuntimeSnapshot,
  safeOrigin,
} from "./environment";
import { TraceRepository } from "./TraceRepository";
import { TraceUploader } from "./TraceUploader";
import {
  TRACE_VERSION,
  type RecordTraceEventInput,
  type TraceWirePayload,
  type TraceAttempt,
  type TraceAttemptState,
  type TraceConnection,
  type TraceConnectionState,
  type StoredTraceEvent,
  type StoredTraceSession,
  type TraceStage,
} from "./types";

const MAX_PAYLOAD_BYTES = 8 * 1024;
const FORBIDDEN_KEYS = /token|password|authorization|cookie|jwt|sdp|secret/i;

const uuid = (): string => crypto.randomUUID();

interface EventContextSnapshot {
  attemptId: string | null;
  parentAttemptId: string | null;
  connectionId: string | null;
  callId: string | null;
  roomId: string | null;
  userId: string | null;
  deviceId: string | null;
}

/** Возвращает безопасное описание ошибки без текста и stack trace. */
export function describeTraceError(error: unknown): Record<string, unknown> {
  if (!(error instanceof Error)) return { errorType: typeof error };
  const candidate = error as Error & {
    status?: unknown;
    httpStatus?: unknown;
    code?: unknown;
    errcode?: unknown;
    category?: unknown;
    reason?: unknown;
    reasonName?: unknown;
  };
  return {
    name: error.name,
    status: typeof candidate.status === "number" ? candidate.status : null,
    httpStatus:
      typeof candidate.httpStatus === "number" ? candidate.httpStatus : null,
    code:
      typeof candidate.code === "string" || typeof candidate.code === "number"
        ? candidate.code
        : null,
    errcode: typeof candidate.errcode === "string" ? candidate.errcode : null,
    category:
      typeof candidate.category === "string" ? candidate.category : null,
    // Строковый reason может быть введён пользователем, поэтому сохраняем только SDK enum.
    reason: typeof candidate.reason === "number" ? candidate.reason : null,
    reasonName:
      typeof candidate.reasonName === "string" ? candidate.reasonName : null,
  };
}

function sanitizeValue(value: unknown, depth = 0): unknown {
  if (depth > 5) return "[depth-limit]";
  if (value === null || typeof value === "boolean" || typeof value === "number")
    return value;
  if (typeof value === "string") return value.slice(0, 512);
  if (Array.isArray(value))
    return value.slice(0, 50).map((item) => sanitizeValue(item, depth + 1));
  if (typeof value === "object") {
    const result: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(
      value as Record<string, unknown>,
    )) {
      if (!FORBIDDEN_KEYS.test(key))
        result[key] = sanitizeValue(item, depth + 1);
    }
    return result;
  }
  return typeof value;
}

/** Очищает payload и гарантирует лимит wire-format. */
export function sanitizeTracePayload<T extends object>(
  payload: T,
): T | { truncated: true; originalKeys: string[] } {
  const sanitized = sanitizeValue(payload) as Record<string, unknown>;
  if (
    new TextEncoder().encode(JSON.stringify(sanitized)).byteLength <=
    MAX_PAYLOAD_BYTES
  )
    return sanitized as T;
  return { truncated: true, originalKeys: Object.keys(sanitized).slice(0, 50) };
}

/** Единственная точка управления текущей session и attempt. */
export class CallTraceJournal {
  public constructor(
    private readonly repository = new TraceRepository(),
    private readonly uploader = new TraceUploader(repository),
  ) {}

  private readonly session: StoredTraceSession = createInitialSession(uuid());
  private startedAt = performance.now();
  private currentAttempt: TraceAttempt | null = null;
  private currentConnection: TraceConnection | null = null;
  private attemptOrdinal = 0;
  private connectionOrdinal = 0;
  private queue: Promise<unknown> = Promise.resolve();
  private started = false;
  private sessionInitialized = false;
  private routeActive = false;
  private activeRoute: string | null = null;
  private pendingRouteLeave: number | null = null;
  private disabled = false;
  private collectionEnabled = true;
  private uploadEndpoint: string | undefined;
  private resolvedUserId: string | null = null;
  private resolvedDeviceId: string | null = null;
  private lastNetworkSnapshot = "";
  private overflowReported = false;

  public get sessionId(): string {
    return this.session.sessionId;
  }

  public get attemptId(): string | null {
    return this.currentAttempt?.attemptId ?? null;
  }

  public start(): void {
    if (this.started) return;
    this.started = true;
  }

  /** Активирует журнал на время нахождения пользователя на call-route. */
  public enterCallRoute(roomIdOrAlias: string | null): () => void {
    if (!roomIdOrAlias || !this.collectionEnabled) return (): void => undefined;
    if (this.pendingRouteLeave !== null) {
      window.clearTimeout(this.pendingRouteLeave);
      this.pendingRouteLeave = null;
    }

    if (this.routeActive && this.activeRoute !== roomIdOrAlias) {
      this.abandonCurrentAttempt("call_route_changed");
    }

    if (!this.routeActive) {
      this.routeActive = true;
      this.startedAt = performance.now();
      const now = new Date().toISOString();
      const runtime = getRuntimeSnapshot();
      Object.assign(this.session, runtime, {
        endedAt: null,
        endReason: null,
        lastEventAt: now,
      });
      if (!this.sessionInitialized) {
        this.sessionInitialized = true;
        Object.assign(this.session, { startedAt: now, createdAt: now });
        this.enqueue(async () =>
          this.repository.initializeSession(this.session),
        );
      } else {
        this.enqueue(async () =>
          this.repository.updateSession(this.sessionId, {
            ...runtime,
            endedAt: null,
            endReason: null,
          }),
        );
      }
      this.attachRuntimeListeners();
      this.enqueue(() =>
        this.uploader.configure(this.collectionEnabled, this.uploadEndpoint),
      );
    }

    this.activeRoute = roomIdOrAlias;
    this.ensureInitialAttempt(roomIdOrAlias);
    return (): void => this.scheduleCallRouteLeave(roomIdOrAlias);
  }

  private attachRuntimeListeners(): void {
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    window.addEventListener("online", this.onNetworkChange);
    window.addEventListener("offline", this.onNetworkChange);
    (
      navigator as Navigator & { connection?: EventTarget }
    ).connection?.addEventListener("change", this.onNetworkChange);
    window.addEventListener("pagehide", this.onPageHide);
  }

  private detachRuntimeListeners(): void {
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    window.removeEventListener("online", this.onNetworkChange);
    window.removeEventListener("offline", this.onNetworkChange);
    (
      navigator as Navigator & { connection?: EventTarget }
    ).connection?.removeEventListener("change", this.onNetworkChange);
    window.removeEventListener("pagehide", this.onPageHide);
  }

  private scheduleCallRouteLeave(roomIdOrAlias: string): void {
    if (!this.routeActive || this.activeRoute !== roomIdOrAlias) return;
    if (this.pendingRouteLeave !== null)
      window.clearTimeout(this.pendingRouteLeave);
    // Нулевая задержка отделяет реальный уход со страницы от remount в StrictMode.
    this.pendingRouteLeave = window.setTimeout(() => {
      this.pendingRouteLeave = null;
      if (!this.routeActive || this.activeRoute !== roomIdOrAlias) return;
      this.abandonCurrentAttempt("call_route_left");
      this.routeActive = false;
      this.activeRoute = null;
      this.detachRuntimeListeners();
      const endedAt = new Date().toISOString();
      this.enqueue(async () =>
        this.repository.updateSession(this.sessionId, {
          endedAt,
          endReason: "normal",
        }),
      );
      this.flush();
      this.uploader.stop();
    }, 0);
  }

  private abandonCurrentAttempt(reasonCode: string): void {
    if (
      this.currentAttempt &&
      !["ended", "failed", "abandoned"].includes(this.currentAttempt.state)
    ) {
      this.finishAttempt("abandoned", "abandoned", "attempt", reasonCode);
    }
  }

  public configure(input: {
    enabled: boolean;
    endpoint?: string;
    homeserver?: string;
    backendBaseUrl?: string;
    livekitBaseUrl?: string;
  }): void {
    if (!input.enabled) {
      this.collectionEnabled = false;
      this.uploadEndpoint = undefined;
      this.uploader.configure(false, undefined);
      if (this.pendingRouteLeave !== null) {
        window.clearTimeout(this.pendingRouteLeave);
        this.pendingRouteLeave = null;
      }
      if (this.routeActive) this.detachRuntimeListeners();
      this.routeActive = false;
      this.activeRoute = null;
      this.currentAttempt = null;
      this.currentConnection = null;
      this.resolvedUserId = null;
      this.resolvedDeviceId = null;
      this.sessionInitialized = false;
      this.enqueue(async () => this.repository.clearAll());
      return;
    }
    this.collectionEnabled = true;
    this.uploadEndpoint = input.endpoint;
    const patch: Partial<StoredTraceSession> = {
      homeserver: safeOrigin(input.homeserver),
      backendBaseUrl: safeOrigin(input.backendBaseUrl),
      livekitBaseUrl: safeOrigin(input.livekitBaseUrl),
    };
    Object.assign(this.session, patch);
    if (this.sessionInitialized) {
      this.enqueue(async () =>
        this.repository.updateSession(this.sessionId, patch),
      );
    }
    if (this.routeActive) {
      this.enqueue(() =>
        this.uploader.configure(input.enabled, input.endpoint),
      );
    }
  }

  /** Создаёт initial attempt на входе в call-route. Повторный React mount безопасен. */
  public ensureInitialAttempt(roomIdOrAlias: string | null): string | null {
    if (!this.routeActive || !roomIdOrAlias) return null;
    if (
      this.currentAttempt &&
      !["ended", "failed", "abandoned"].includes(this.currentAttempt.state)
    ) {
      return this.currentAttempt.attemptId;
    }
    return this.createAttempt(
      "initial_join",
      "call_route",
      null,
      roomIdOrAlias.startsWith("!") ? roomIdOrAlias : null,
    );
  }

  /** Создаёт дочернюю attempt только для явного rejoin. */
  public startManualRejoin(): string | null {
    if (!this.routeActive) return null;
    const parent = this.currentAttempt;
    if (!parent) return null;
    if (!["ended", "failed", "abandoned"].includes(parent.state)) {
      this.finishAttempt("failed", "fatal_failure", "call", "manual_rejoin");
    }
    return this.createAttempt(
      "manual_rejoin",
      "error_recovery",
      parent.attemptId,
      parent.roomId,
    );
  }

  private createAttempt(
    kind: TraceAttempt["kind"],
    trigger: TraceAttempt["trigger"],
    parentAttemptId: string | null,
    roomId: string | null,
  ): string {
    const attempt: TraceAttempt = {
      attemptId: uuid(),
      sessionId: this.sessionId,
      projectionRevision: 0,
      parentAttemptId,
      ordinal: ++this.attemptOrdinal,
      kind,
      trigger,
      startedAt: new Date().toISOString(),
      endedAt: null,
      state: "preparing",
      finalOutcome: null,
      terminalStage: null,
      reasonCode: null,
      roomId,
      userId: this.resolvedUserId,
      deviceId: this.resolvedDeviceId,
    };
    this.currentAttempt = attempt;
    this.currentConnection = null;
    this.connectionOrdinal = 0;
    this.enqueue(async () => this.repository.putAttempt(attempt));
    this.record({
      eventType: "attempt.started",
      stage: "attempt",
      outcome: "started",
      payload: { kind, trigger },
    });
    return attempt.attemptId;
  }

  public setIdentity(userId: string | null, deviceId: string | null): void {
    this.resolvedUserId = userId;
    this.resolvedDeviceId = deviceId;
    this.patchAttempt({ userId, deviceId });
    this.record({
      eventType: "attempt.identity_resolved",
      stage: "attempt",
      outcome: "succeeded",
    });
  }

  public setRoom(roomId: string): void {
    this.patchAttempt({ roomId });
    this.record({
      eventType: "attempt.room_resolved",
      stage: "attempt",
      outcome: "succeeded",
    });
  }

  /** Открывает новый технический цикл подключения внутри текущей attempt. */
  public startConnection(trigger: TraceConnection["trigger"]): string | null {
    const attempt = this.currentAttempt;
    if (!this.routeActive || !attempt) return null;
    if (
      this.currentConnection &&
      !["disconnected", "failed", "superseded"].includes(
        this.currentConnection.state,
      )
    ) {
      return this.currentConnection.connectionId;
    }
    const connection: TraceConnection = {
      connectionId: uuid(),
      sessionId: this.sessionId,
      attemptId: attempt.attemptId,
      projectionRevision: 0,
      ordinal: ++this.connectionOrdinal,
      trigger,
      startedAt: new Date().toISOString(),
      endedAt: null,
      state: "connecting",
      callId: null,
      reasonCode: null,
    };
    this.currentConnection = connection;
    this.enqueue(async () => this.repository.putConnection(connection));
    this.record({
      eventType: "livekit.connection_segment_started",
      stage: "livekit",
      outcome: "started",
      payload: { trigger, ordinal: connection.ordinal },
    });
    return connection.connectionId;
  }

  /** Связывает текущий segment с server-side SID LiveKit room. */
  public resolveCallId(callId: string): void {
    if (!callId || !this.currentAttempt) return;
    if (["ended", "failed", "abandoned"].includes(this.currentAttempt.state))
      return;
    if (!this.currentConnection) this.startConnection("initial_connect");
    const connection = this.currentConnection;
    if (!connection) return;
    if (connection.callId === callId) return;
    if (connection.callId !== null) {
      this.finishConnection("superseded", "call_id_changed");
      this.startConnection("call_id_changed");
    }
    this.patchConnection({ callId });
    this.record({
      eventType: "livekit.call_id_resolved",
      stage: "livekit",
      outcome: "succeeded",
      payload: { callId },
    });
  }

  public markConnectionConnected(): void {
    if (this.currentConnection?.state !== "connecting") return;
    this.patchConnection({ state: "connected" });
  }

  public finishConnection(
    state: Exclude<TraceConnectionState, "connecting" | "connected">,
    reasonCode: string | null,
  ): void {
    const connection = this.currentConnection;
    if (
      !connection ||
      ["disconnected", "failed", "superseded"].includes(connection.state)
    )
      return;
    this.patchConnection({
      state,
      reasonCode,
      endedAt: new Date().toISOString(),
    });
    this.record({
      eventType: "livekit.connection_segment_ended",
      stage: "livekit",
      severity: state === "failed" ? "error" : "info",
      outcome:
        state === "failed"
          ? "failed"
          : state === "superseded"
            ? "state_changed"
            : "cancelled",
      reasonCode,
      payload: { state, reasonCode },
    });
  }

  private patchConnection(patch: Partial<TraceConnection>): void {
    if (!this.routeActive || !this.currentConnection) return;
    Object.assign(this.currentConnection, patch);
    const connectionId = this.currentConnection.connectionId;
    this.enqueue(async () =>
      this.repository.updateConnection(connectionId, patch),
    );
  }

  public setAttemptState(state: TraceAttemptState): void {
    this.patchAttempt({ state });
  }

  public markConnected(): void {
    if (this.currentAttempt?.state === "connected") return;
    this.patchAttempt({ state: "connected" });
    this.record({
      eventType: "attempt.connected",
      stage: "attempt",
      outcome: "succeeded",
    });
  }

  public finishAttempt(
    state: "ended" | "failed" | "abandoned",
    finalOutcome: NonNullable<TraceAttempt["finalOutcome"]>,
    terminalStage: TraceStage,
    reasonCode: string,
  ): void {
    const attempt = this.currentAttempt;
    if (!attempt || ["ended", "failed", "abandoned"].includes(attempt.state))
      return;
    this.finishConnection(
      state === "failed" ? "failed" : "disconnected",
      reasonCode,
    );
    const patch: Partial<TraceAttempt> = {
      state,
      finalOutcome,
      terminalStage,
      reasonCode,
      endedAt: new Date().toISOString(),
    };
    this.patchAttempt(patch);
    this.record({
      eventType:
        state === "ended"
          ? "attempt.ended"
          : state === "failed"
            ? "attempt.failed"
            : "attempt.abandoned",
      stage: "attempt",
      severity: state === "failed" ? "error" : "info",
      outcome:
        state === "failed"
          ? "failed"
          : state === "abandoned"
            ? "cancelled"
            : "succeeded",
      reasonCode,
      payload: { finalOutcome, terminalStage },
    });
    this.flush();
  }

  private patchAttempt(patch: Partial<TraceAttempt>): void {
    if (!this.routeActive || !this.currentAttempt) return;
    Object.assign(this.currentAttempt, patch);
    const attemptId = this.currentAttempt.attemptId;
    this.enqueue(async () => this.repository.updateAttempt(attemptId, patch));
  }

  /** Добавляет событие в очередь записи и сразу возвращает eventId для correlation. */
  public record(input: RecordTraceEventInput): string {
    const eventId = uuid();
    if (
      !this.started ||
      !this.routeActive ||
      this.disabled ||
      !this.collectionEnabled
    )
      return eventId;
    const context = this.snapshotContext();
    this.enqueue(async () => this.persistEvent(input, eventId, context));
    return eventId;
  }

  private snapshotContext(): EventContextSnapshot {
    const attempt = this.currentAttempt;
    return {
      attemptId: attempt?.attemptId ?? null,
      parentAttemptId: attempt?.parentAttemptId ?? null,
      connectionId: this.currentConnection?.connectionId ?? null,
      callId: this.currentConnection?.callId ?? null,
      roomId: attempt?.roomId ?? null,
      userId: attempt?.userId ?? null,
      deviceId: attempt?.deviceId ?? null,
    };
  }

  private async persistEvent(
    input: RecordTraceEventInput,
    eventId = uuid(),
    context = this.snapshotContext(),
  ): Promise<void> {
    if (this.disabled) return;
    const usage = await this.repository.bufferUsage();
    const overLimit =
      usage.events >= TraceRepository.limits.maxEvents ||
      usage.bytes >= TraceRepository.limits.maxBytes;
    if (
      overLimit &&
      (input.severity ?? "info") !== "warn" &&
      !["error", "fatal"].includes(input.severity ?? "info")
    ) {
      if (!this.overflowReported) {
        this.overflowReported = true;
        await this.persistEvent(
          {
            eventType: "telemetry.buffer_overflow",
            stage: "telemetry",
            severity: "error",
            outcome: "failed",
            reasonCode: "local_limit_reached",
            payload: usage,
          },
          undefined,
          context,
        );
      }
      return;
    }
    if (
      usage.bytes >=
        TraceRepository.limits.maxBytes +
          TraceRepository.limits.reservedImportantBytes &&
      input.eventType !== "telemetry.buffer_overflow"
    )
      return;
    const payload = sanitizeTracePayload(
      input.payload ?? {},
    ) as TraceWirePayload;
    const timestamp = new Date().toISOString();
    const base: Omit<StoredTraceEvent, "sequence" | "approximateBytes"> = {
      eventId,
      sessionId: this.sessionId,
      attemptId: context.attemptId,
      parentAttemptId: context.parentAttemptId,
      connectionId: context.connectionId,
      callId: context.callId,
      roomId: context.roomId,
      userId: context.userId,
      deviceId: context.deviceId,
      timestamp,
      sessionElapsedMs: Math.round(performance.now() - this.startedAt),
      eventType: input.eventType,
      stage: input.stage,
      severity: input.severity ?? "info",
      outcome: input.outcome ?? "none",
      reasonCode: input.reasonCode ?? null,
      message: input.message?.slice(0, 512) ?? null,
      payload,
      relatedEventId: input.relatedEventId ?? null,
      durationMs: input.durationMs ?? null,
      traceVersion: TRACE_VERSION,
      deliveryState: "pending",
    };
    const approximateBytes = new TextEncoder().encode(
      JSON.stringify(base),
    ).byteLength;
    await this.repository.appendEvent({ ...base, approximateBytes });
    if ((await this.repository.pendingCount()) >= 25)
      void this.uploader.flush();
  }

  public flush(useBeacon = false): void {
    void this.queue.then(async () => this.uploader.flush(useBeacon));
  }

  private enqueue(operation: () => unknown): void {
    this.queue = this.queue.then(operation).catch((error) => {
      logger.warn(
        "[CallTrace] Операция журнала не выполнена",
        describeTraceError(error),
      );
      if (
        error instanceof DOMException &&
        ["SecurityError", "NotSupportedError"].includes(error.name)
      ) {
        this.disabled = true;
      }
    });
  }

  private readonly onVisibilityChange = (): void => {
    this.record({
      eventType: "app.visibility_changed",
      stage: "app",
      outcome: "state_changed",
      payload: { visibilityState: document.visibilityState },
    });
    if (document.visibilityState === "hidden") this.flush();
  };

  private readonly onNetworkChange = (): void => {
    const snapshot = getRuntimeSnapshot();
    const signature = JSON.stringify(snapshot);
    if (signature === this.lastNetworkSnapshot) return;
    this.lastNetworkSnapshot = signature;
    this.record({
      eventType: "app.network_state_changed",
      stage: "app",
      outcome: "state_changed",
      payload: { ...snapshot },
    });
  };

  private readonly onPageHide = (event: PageTransitionEvent): void => {
    if (event.persisted) {
      this.flush(true);
      return;
    }
    const endedAt = new Date().toISOString();
    this.enqueue(async () =>
      this.repository.updateSession(this.sessionId, {
        endedAt,
        endReason: "page_unload",
      }),
    );
    this.flush(true);
  };
}

export const callTraceJournal = new CallTraceJournal();
